import { useQueryClient } from "@tanstack/react-query";
import {
  FolderKanban,
  Gauge,
  LayoutDashboard,
  LayoutGrid,
  LineChart,
  ListChecks,
  type LucideIcon,
  Menu,
  Monitor,
  Moon,
  Receipt,
  Search,
  Settings,
  Sun,
} from "lucide-react";
import { useCallback, useSyncExternalStore } from "react";
import { Link, NavLink } from "react-router-dom";

import { APPS_QUERY_KEY } from "@/api/apps";
import { useSession } from "@/api/auth";
import { type Project, type ProjectListResponse, PROJECTS_QUERY_KEY } from "@/api/projects";
import { UserMenu } from "@/components/layout/UserMenu";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { formatCount } from "@/lib/format-count";
import { cn } from "@/lib/utils";
import { useSidebarStore } from "@/stores/sidebar";
import { applyThemeClass, resolveTheme, useThemeStore } from "@/stores/theme";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

/** Ties the visible "Workspace" heading to the list it names. */
const WORKSPACE_LABEL_ID = "rail-workspace-label";

const NAV_ITEMS: NavItem[] = [
  // flowstack#945 — the landing surface, and so the first rail entry:
  // apps, projects with work in them, and the latest runs. It is a
  // summary of the destinations below it, which is why it sits above
  // them rather than among them.
  //
  // Icon note: ``LayoutDashboard`` was already spoken for by Artifacts
  // when this landed. Rather than reshuffle two rail icons from a
  // dispatch told not to restyle the chrome (#953 is queued and owns
  // vertical space here), Dashboard takes ``Gauge`` and Artifacts is
  // left untouched.
  { to: "/dashboard", label: "Dashboard", icon: Gauge },
  // M9.5 — "Apps" is the entry point to the ATTMOS Suite Unification
  // launcher (epic #340). Sits above Projects per the M9.5 plan:
  // the launcher is the cross-product entry point, Projects is the
  // FlowStack-internal workflow surface.
  { to: "/apps", label: "Apps", icon: LayoutGrid },
  { to: "/projects", label: "Projects", icon: FolderKanban },
  { to: "/runs", label: "Runs", icon: ListChecks },
  // VIZ U4.1 (D71-iii's missed half): the app-level analysis surface,
  // project-scoped — pick a project, see its runs' analyses. The per-run
  // Analysis tab (#922) lives at RunDetail.tsx; this is the top-level
  // entry point a user without a run id in hand needs.
  { to: "/analysis", label: "Analysis", icon: LineChart },
  { to: "/artifacts", label: "Artifacts", icon: LayoutDashboard },
  { to: "/budget", label: "Budget", icon: Receipt },
  // Settings is deliberately NOT here — it moved to the account group
  // at the foot of the rail, beside the theme toggle and the user
  // menu. Navigation is "where in the product am I going"; settings /
  // theme / identity are "who and how am I", and the operator asked
  // for the second set grouped together in the rail.
];

/**
 * Shared shape for everything that behaves like a rail row.
 *
 * ``relative`` is what the active row's left edge marker anchors to —
 * it is a ``::before`` on ``.rail-row-active`` positioned into the
 * list's own padding, so without a positioned ancestor here it would
 * escape to the nearest one and land somewhere else entirely.
 */
const RAIL_ROW = [
  "relative flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium",
  "text-sidebar-foreground",
].join(" ");

/**
 * The hover surface, SEPARATE from `RAIL_ROW` — and it has to be.
 *
 * Tailwind emits `hover:bg-sidebar-hover` into `@layer utilities` and
 * `.rail-row-active` into `@layer components`, and a later layer beats
 * an earlier one whatever the specificity. So a row carrying both loses
 * its wash the moment the pointer touches it — the active row would
 * flicker back to looking ordinary exactly when a user is aiming at it,
 * with only the edge marker left. Adding a `hover:` override on the
 * active row does not fix it either: `cn` runs tailwind-merge, which
 * would resolve the two `hover:bg-*` utilities to whichever came last.
 *
 * So the active row simply never gets this class, and every other row
 * composes it explicitly.
 */
const RAIL_ROW_HOVER = "hover:bg-sidebar-hover";

/**
 * Every app the Apps page lists: the cached `/api/apps` response's entitled
 * portals plus its in-app cards (absent from an older API, so counted as
 * none). Honest because the response holds both lists WHOLE.
 */
function appCount(cached: unknown): number | undefined {
  if (typeof cached !== "object" || cached === null) return undefined;
  const { apps, in_app } = cached as { apps?: unknown; in_app?: unknown };
  if (!Array.isArray(apps)) return undefined;
  return apps.length + (Array.isArray(in_app) ? in_app.length : 0);
}

/**
 * The cached projects list, but only if it is provably WHOLE: the
 * cached `/api/projects` response holds exactly `total` rows.
 *
 * `total` is the server's own count of the tenant's projects, separate
 * from the list (flowstack#1866), so a list cut short by a page size, a
 * cap or a filter fails `projects.length === total` and yields
 * `undefined`. So does a response without a numeric `total` (an older
 * server): the list might be whole, but nothing proves it, and a badge
 * must never guess.
 */
function wholeProjects(cached: unknown): Array<Partial<Project>> | undefined {
  const response = cached as Partial<ProjectListResponse> | undefined;
  const projects = response?.projects;
  if (!Array.isArray(projects) || typeof response?.total !== "number") return undefined;
  return projects.length === response.total ? projects : undefined;
}

/** How many projects the tenant has, shown only for a whole list. */
function projectCount(cached: unknown): number | undefined {
  return wholeProjects(cached)?.length;
}

/**
 * Every run in the tenant: the sum of `run_count` over the cached
 * projects list, each a real `COUNT(*)` since flowstack#961.
 *
 * NOT the length of `RUNS_QUERY_KEY`. That entry is one `/api/runs`
 * page, capped at the route's `limit` (200 by default), so its length
 * is a page size — the rail once read "Runs 200" beside a Projects card
 * saying 1542 (flowstack#1866). A project row without a numeric
 * `run_count` yields `undefined` rather than a partial sum, because a
 * short total is the same wrong number in a different place.
 *
 * Two conditions make the sum the tenant's total:
 *
 * - **The projects list is WHOLE.** Summing a capped page of projects
 *   is the #1866 bug one level up. This is checked at run time, not
 *   assumed: `wholeProjects` returns the list only when its length
 *   equals the response's `total`, and otherwise there is no badge.
 * - **Every run belongs to a listed project.** A run owned by no listed
 *   project is in no `run_count`, so the sum undercounts it silently.
 *   True today (one pilot project owns every run); multi-project
 *   scoping must keep it true, or this badge must read a server-side
 *   tenant total instead.
 */
function totalRunCount(cached: unknown): number | undefined {
  const projects = wholeProjects(cached);
  if (projects === undefined) return undefined;
  let total = 0;
  for (const project of projects) {
    if (typeof project?.run_count !== "number") return undefined;
    total += project.run_count;
  }
  return total;
}

/**
 * A count derived from what the client ALREADY HOLDS under `key`, or
 * `undefined` — `read` turns the cached value into the number.
 *
 * The rail shows a count badge only where the number is genuinely
 * known, and this hook is what makes "known" mean something. It reads
 * the TanStack cache and subscribes to it; it never fetches, and it
 * creates no query observer, so a rail on a page that has not loaded
 * the entry neither triggers a request nor keeps a dead entry alive
 * past its `gcTime`. A badge is therefore present exactly while the
 * client is holding the entry, and gone otherwise — a placeholder zero
 * would be worse than no badge, reading as "zero results" rather than
 * "not loaded".
 *
 * Two consequences worth knowing rather than discovering:
 *
 * - The number is as fresh as the last fetch of that entry. Nothing
 *   here refetches, so submitting a run moves the Runs badge when
 *   something re-reads `/api/projects`, not at the moment of submission.
 * - A length is a count only for a WHOLE list. `ARTIFACTS_QUERY_KEY`
 *   and `RUNS_QUERY_KEY` each cache one capped page, so their lengths
 *   are page sizes wearing a total's clothes. Artifacts therefore gets
 *   no badge, and Runs reads its total from the projects list instead
 *   (`totalRunCount`).
 *
 * `read` must be a stable reference (a module-level function): it is a
 * dependency of `getSnapshot`.
 *
 * `getSnapshot` returns a number or `undefined`, never a new object:
 * `useSyncExternalStore` compares snapshots by identity, so returning
 * a fresh `{count}` on every cache event anywhere in the app would
 * re-render the rail forever.
 *
 * The subscription is SCOPED to the one key this hook reads (Rupert
 * PR-1147 r0 Summary #3). `QueryCache.subscribe` is cache-wide, so an
 * unfiltered subscriber wakes on every query event in the app — three
 * of them, on a rail that is mounted on every page. Identity-stable
 * snapshots keep that from re-rendering, but "it does not re-render"
 * is not the same as "it does not run": the filter is what makes the
 * cost proportional to the thing being watched.
 */
function useCachedCount(
  key: readonly unknown[],
  read: (cached: unknown) => number | undefined
): number | undefined {
  const client = useQueryClient();
  // The hash `getQueryData` will itself look this key up by — taken
  // from the client rather than from `hashKey` directly, so a custom
  // `queryKeyHashFn` in the client's defaults can never make the
  // filter and the read disagree (which would present as a badge that
  // renders once and then silently stops updating).
  const { queryHash } = client.defaultQueryOptions({ queryKey: key });
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      client.getQueryCache().subscribe((event) => {
        if (event.query.queryHash === queryHash) onStoreChange();
      }),
    [client, queryHash]
  );
  const getSnapshot = useCallback(
    (): number | undefined => read(client.getQueryData(key)),
    [client, key, read]
  );
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * The left rail — the app's ONLY chrome. Everything right of it is
 * pure workspace and carries no global controls.
 *
 * **It spans the viewport top to bottom** (flowstack#953) and, since
 * flowstack#1002, it is the whole frame: the top bar is gone and its
 * three occupants live here. The rail therefore has three zones,
 * top to bottom:
 *
 * 1. **Brand** — the FlowStack lockup (a link home), the ``M9 pilot``
 *    build marker, and the collapse toggle.
 * 2. **Navigation** — ``<nav aria-label="Primary">``, destinations only.
 * 3. **Account** — the app-level controls: ⌘K, theme, Settings, you.
 *
 * The split the operator drew is **rail = you and the app; page =
 * this thing** (#1002). ⌘K searches the whole app, so it is an
 * app-level control and belongs in zone 3 beside the theme cycler —
 * not in a page's action row, which would make an app-wide affordance
 * look page-scoped. Page-specific verbs belong to the page.
 *
 * ``shrink-0`` because the rail's width is a state (``w-14`` /
 * ``w-56``), not a hint — a wide table in the workspace must not
 * squeeze it. ``h-full`` and the shell row's default ``align-items:
 * stretch`` say the same thing; the explicit height is kept so the
 * rail does not silently collapse if a future edit sets
 * ``items-start`` on the shell.
 *
 * Two things make it a frame rather than a decorated nav:
 *
 * 1. It is a TINTED surface (``bg-sidebar``), not ``bg-background``.
 *    Before this it was literally the same surface as the content
 *    beside it, so the app read as one flat sheet. The tint is
 *    per-theme and derived from each theme's own neutrals — see the
 *    ``--sidebar*`` block in globals.css for the measured contrast.
 * 2. It carries every control that is not about the thing on screen —
 *    identity, appearance, search, and now the brand anchor.
 *
 * Collapsed (``w-14``) every label goes ``sr-only`` and the rows
 * degrade to centred icons; the account group survives because each
 * of its controls is icon-first with the label as the disclosure,
 * exactly like the nav rows above it.
 *
 * **Landmarks.** The rail is an ``<aside>``; the ``<nav
 * aria-label="Primary">`` inside it wraps ONLY the navigation list.
 * The brand zone and the account group are siblings of that nav, not
 * children: a lockup, a collapse toggle, a theme cycler and an account
 * menu are not destinations, and putting them in the navigation
 * landmark makes a screen reader announce them as such (Rupert
 * PR-948 r0). ``<aside>`` rather than a bare ``<div>`` so they still
 * sit inside SOME landmark — a plain div would fix the mislabel by
 * orphaning the content instead.
 */
export function Sidebar() {
  const collapsed = useSidebarStore((s) => s.collapsed);
  const toggleSidebar = useSidebarStore((s) => s.toggle);
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const { data: session } = useSession();
  const resolvedTheme = resolveTheme(theme);

  // The three destinations whose count the client holds in full, and
  // so the three that can carry an honest badge: Apps is a list cached
  // WHOLE, Projects is the projects list once its `total` proves it
  // whole, and Runs is that list's `run_count` total — never the length
  // of the capped `/api/runs` page (flowstack#1866). Read as fixed hook calls rather than inside the
  // `NAV_ITEMS` map, which would be a hook in a loop. Everything absent
  // from this map gets no badge, on purpose:
  //
  //   Dashboard  a summary of the surfaces below it — there is no
  //              "number of dashboards" to state.
  //   Analysis   project-scoped (`projectRunsQueryKey`); there is no
  //              tenant-wide analyses list to count without a fetch.
  //   Artifacts  the cached entry is ONE page (`ARTIFACT_PAGE_LIMIT`),
  //              so its length is a page size, not a total. A badge
  //              reading "200" beside a catalog of thousands is a
  //              wrong number, which is worse than a missing one.
  //   Budget     a ledger rollup, not a list of things.
  const counts: Record<string, number | undefined> = {
    "/apps": useCachedCount(APPS_QUERY_KEY, appCount),
    "/projects": useCachedCount(PROJECTS_QUERY_KEY, projectCount),
    "/runs": useCachedCount(PROJECTS_QUERY_KEY, totalRunCount),
  };

  // Same three-way cycle the header carried; relocated, not redesigned.
  const cycleTheme = (): void => {
    const next = theme === "light" ? "dark" : theme === "dark" ? "system" : "light";
    setTheme(next);
    applyThemeClass(next);
  };

  const openPalette = (): void => {
    // Dispatch the cmd-K hotkey so the listener in RunCommandPalette
    // picks it up — keeps a single source of truth for "open palette."
    window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true }));
  };

  // "system" is a distinct state from the theme it currently resolves
  // to, so it gets its own icon — a Sun on a system-light machine
  // would say the user picked light, which they did not.
  const ThemeIcon = theme === "system" ? Monitor : resolvedTheme === "dark" ? Moon : Sun;
  const themeLabel =
    theme === "system" ? `System (${resolvedTheme})` : theme === "dark" ? "Dark" : "Light";

  return (
    <aside
      aria-label="Sidebar"
      className={cn(
        // ``app-rail`` re-points --ring at --sidebar-ring for the
        // whole subtree; see globals.css.
        "app-rail flex h-full shrink-0 flex-col border-r border-sidebar-border bg-sidebar",
        "text-sidebar-foreground transition-[width] duration-200",
        collapsed ? "w-14" : "w-56"
      )}
    >
      {/* Brand zone — the answer to #982's objection that a lockup at
          the head of the rail "vanishes at w-14". It does not: the
          asset is a 4:1 mark-plus-wordmark, so at w-14 the wordmark is
          cropped away and the MARK stays, at its own size and aspect.
          One asset, two forms — no second file to drift from the brand.

          The collapse toggle lives here rather than in a bar because
          #982's other objection ("a control cannot live inside the
          thing it collapses") only holds if the collapsed rail has no
          controls. It has: at w-14 the zone stacks, and the toggle is
          a full 40px target directly under the mark. */}
      <div className="flex shrink-0 flex-col gap-1 border-b border-sidebar-border p-2">
        <div className={cn("flex items-center gap-2", collapsed ? "flex-col" : "justify-between")}>
          <Link to="/" aria-label="FlowStack home" className="flex items-center">
            {/* Theme-aware lockup: white-Flow on dark, navy-Flow on
                light — same convention as pharmaforge's TopBar.tsx
                logo. Both PNGs share the 4:1 aspect of the Cognito
                Hosted UI lockup, so the user reads the same brand on
                sign-in and inside the SPA.

                Collapsed, the box narrows to ``w-7`` and clips: the
                img still renders whole at its natural ratio (``h-8
                w-auto`` = 128x32) and ``overflow-hidden`` shows only
                the left of it. The window is measured, not eyeballed
                — in the 820x205 source the mark occupies x 10..164 and
                the ``F`` starts at x 186, which at 128px wide is
                25.6px and 29.0px. ``w-7`` (28px) is the only Tailwind
                step inside that gap: the whole mark, none of the
                wordmark. Re-measure it if the asset is ever recut.

                ``max-w-none`` is load-bearing: Tailwind's preflight
                sets ``max-width: 100%`` on img, which would shrink the
                128px-wide image into the 28px box and paint the entire
                lockup at 28x7 — which IS the vanishing act #982
                warned about, only harder to spot. */}
            <span
              className={cn("block overflow-hidden", collapsed ? "h-8 w-7" : "h-8 w-32")}
              data-testid="rail-lockup"
            >
              <img
                src={
                  resolvedTheme === "dark"
                    ? "/flowstack-logo-white.png"
                    : "/flowstack-logo-navy.png"
                }
                alt="FlowStack"
                className="h-8 w-auto max-w-none"
              />
            </span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Toggle sidebar"
            onClick={() => toggleSidebar()}
            className="shrink-0"
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </Button>
        </div>
        {/* The build marker is a caption under the lockup rather than
            beside it: the rail is 224px expanded, and the lockup plus
            a 40px toggle already spends 176px of it. It goes with the
            wordmark at w-14 — a bare "M9 pilot" under a cropped mark
            reads as a label for the mark, not a build. */}
        {!collapsed ? <span className="label-micro px-1 text-sidebar-muted">M9 pilot</span> : null}
      </div>

      {/* The nav, not the rail, is what scrolls when the window is
          short. The brand zone made the rail ~72px taller, which is
          enough to overflow a ~500px-tall window — and an <aside> that
          overflows its own height pushes the DOCUMENT into a
          scrollbar, breaking the "<main> is the only scroll container"
          invariant AppShell.tsx and PR-334 r2 #1 defend. Clamping here
          keeps the brand and the account controls pinned at the two
          ends and gives the middle the overflow, which is also the
          right priority: the destinations are the repeatable part.
          ``min-h-0`` because a flex child defaults to ``min-height:
          auto`` and would refuse to shrink. */}
      <nav aria-label="Primary" className="min-h-0 flex-1 overflow-y-auto">
        {/* The section label the mock puts over the primary group. It
            is a real heading rather than a styled span, and the list
            is named BY it — a rail whose only landmark label is
            "Primary" says where the group is but not what it holds.
            It goes `sr-only` at w-14 rather than being removed, the
            same rule every other label in this rail follows: the
            accessible structure must not change when the rail
            narrows.

            `label-micro` carries the whole treatment (size, tracking,
            case, weight) from the one place it is defined — this file
            names only the ink. `type-scale.test.ts` fails the build if
            a second copy of those numbers appears in `src`. */}
        <h2
          id={WORKSPACE_LABEL_ID}
          className={cn("label-micro px-3 pb-1 pt-2 text-sidebar-muted", collapsed && "sr-only")}
        >
          Workspace
        </h2>
        <ul aria-labelledby={WORKSPACE_LABEL_ID} className="flex flex-col gap-1 p-2 pt-1">
          {NAV_ITEMS.map((item) => {
            const count = counts[item.to];
            return (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      RAIL_ROW,
                      // Accent WASH plus a left edge marker, both from
                      // `.rail-row-active` in globals.css — see there
                      // for why the state is not carried by hue alone
                      // and why the marker sits outside the wash. The
                      // ink stays `--sidebar-foreground` (the row's
                      // default), which measures 9.82:1 light /
                      // 10.99:1 dark on the wash.
                      isActive ? "rail-row-active" : RAIL_ROW_HOVER
                    )
                  }
                >
                  <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className={cn(collapsed && "sr-only")}>{item.label}</span>
                  {/* Right-aligned count, and ONLY where the client
                      already holds the number — see `useCachedCount`
                      for which destinations qualify and why the rest
                      are deliberately bare.

                      Hidden at w-14: a bare number under a cropped
                      icon labels the icon, not a list.

                      `aria-hidden` is a decision, not an oversight. It
                      keeps the link's accessible name the DESTINATION
                      ("Runs", not "Runs 500") — a screen-reader user
                      moving through the Primary landmark is choosing
                      where to go, and an unqualified integer glued to
                      the name is noise there. The count is not lost:
                      the page it points at states it in full, with the
                      units this badge has no room for. `tabular-nums`
                      so the column does not shuffle as digits
                      change. */}
                  {!collapsed && count !== undefined ? (
                    <span
                      aria-hidden="true"
                      data-testid={`rail-count-${item.label.toLowerCase()}`}
                      className="ml-auto text-micro tabular-nums text-sidebar-muted"
                    >
                      {formatCount(count)}
                    </span>
                  ) : null}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Account group — pinned to the foot, separated from the
          navigation list by ``mt-auto`` + a rule. Order is
          least-to-most personal: the app, then appearance, then
          preferences, then identity. ⌘K joins at the top of it
          (#1002): it searches the whole app, which makes it an
          app-level control and not a page one.

          A labelled ``group`` rather than a second ``<nav>``: only one
          of the four children is a link, so calling it navigation
          would repeat the mislabel this structure exists to fix. */}
      <div
        role="group"
        aria-label="Account"
        className="mt-auto flex flex-col gap-1 border-t border-sidebar-border p-2"
        data-testid="rail-account-group"
      >
        {/* The search row is a bordered CARD, not another flat row —
            the one affordance in the rail that takes input rather than
            navigating, so the mock gives it an input's outline. The
            border does that work on its own here: the mock fills the
            card with its panel surface, and this frame has exactly one
            step above the rail (`--sidebar-hover`) which hover already
            owns. Borrowing it for the resting state would leave hover
            with nowhere to go, and inventing a fill would mean a new
            colour value — which Tier 1's one-file revert has to
            survive. Stated as a delta from the mock in the PR.

            `mb-1` sets it slightly apart from the controls under it,
            per the mock's `.kbd { margin-bottom }`. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={openPalette}
              aria-label="Open command palette"
              className={cn(RAIL_ROW, RAIL_ROW_HOVER, "mb-1 border border-sidebar-border")}
            >
              <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className={cn(collapsed && "sr-only")}>Search</span>
              {/* The chip is the mock's keycap: mono, outlined, at the
                  micro step read from the scale rather than respelt. */}
              {!collapsed ? (
                // NOT through `cn`, and this is a trap rather than a
                // style preference: `cn` runs tailwind-merge, whose
                // conflict map does not know `micro` is a fontSize key
                // — it is a custom step — so it reads `text-micro` as a
                // text COLOUR and `text-sidebar-muted` silently wins,
                // dropping the size. The class list is static here, so
                // there is nothing for `cn` to resolve anyway.
                // `.label-micro` elsewhere sidesteps this by being a
                // component class rather than a utility.
                <kbd className="ml-auto rounded-sm border border-sidebar-border px-1.5 font-mono text-micro text-sidebar-muted">
                  ⌘K
                </kbd>
              ) : null}
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">Search &amp; quick actions</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={cycleTheme}
              aria-label={`Theme: ${theme}. Click to cycle.`}
              className={cn(RAIL_ROW, RAIL_ROW_HOVER)}
            >
              <ThemeIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className={cn(collapsed && "sr-only")}>{themeLabel}</span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">Toggle theme</TooltipContent>
        </Tooltip>

        <NavLink
          to="/settings"
          className={({ isActive }) =>
            // Settings is a destination too — same active treatment as
            // the nav rows above, so the rail answers "where am I" the
            // same way wherever the answer is.
            cn(RAIL_ROW, isActive ? "rail-row-active" : RAIL_ROW_HOVER)
          }
        >
          <Settings className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className={cn(collapsed && "sr-only")}>Settings</span>
        </NavLink>

        {/* UserMenu is relocated verbatim — same trigger, same menu,
            same tests. Only the popover's anchor changes, because a
            menu opening ``align="end"`` from the FOOT of a LEFT rail
            would open off-screen. */}
        {session ? (
          <div
            className={cn(
              "flex items-center gap-2 border-t border-sidebar-border px-1 pt-2",
              collapsed && "justify-center"
            )}
          >
            <UserMenu session={session} side="top" tone="rail" align="start" />
            {!collapsed ? (
              <div className="min-w-0">
                <div className="truncate text-xs text-sidebar-foreground" title={session.email}>
                  {session.display_name?.trim() || session.email}
                </div>
                {/* Tenant line with its status dot.

                    WHAT THE DOT MEANS, because a green dot that is
                    always green is decoration pretending to be
                    telemetry: it IDENTIFIES the tenant this line
                    names as the one you are signed in to. It is NOT
                    evidence the session is alive. This is chrome — it
                    renders `useSession()`'s locally-held state and
                    performs no liveness check, so a server-side
                    expiry or revocation leaves the dot unchanged
                    until something else refetches. Do not restate it
                    as "the browser holds a LIVE session" (Rupert
                    PR-1147 r0 Summary #1); `docs/source/user/
                    portal-navigation.rst` is worded to match.

                    What the dot CAN honestly rest on is that the
                    whole block renders only when `useSession()` has a
                    session: when the session is absent or errored
                    there is no row here at all, rather than a row
                    with a reassuring dot on it. The `sr-only` prefix
                    carries the identifying meaning to assistive tech,
                    since a coloured circle carries none.

                    It is `--state-completed-FG`, not `--state-completed`,
                    and that is measured rather than a preference: the
                    base token is the chip FILL, tuned to sit under
                    `--state-completed-bg`, and against the light rail
                    it scores 2.16:1 — under the 3:1 bar WCAG 1.4.11
                    sets for a non-text indicator. The `-fg` step is
                    the same semantic green as INK, and it clears the
                    bar in both themes (4.69:1 light, 9.22:1 dark).
                    Same green, right end of it. */}
                <div className="flex items-center gap-1.5">
                  <span
                    aria-hidden="true"
                    className="h-1.5 w-1.5 shrink-0 rounded-full bg-state-completed-fg"
                  />
                  <span className="sr-only">Signed in to </span>
                  <span
                    className="label-micro truncate text-sidebar-muted"
                    title={session.tenant_display_name || session.tenant_slug}
                  >
                    {session.tenant_display_name || session.tenant_slug}
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </aside>
  );
}
